import { c as _c } from "react/compiler-runtime";
import { MarkedText } from '@/components/Atoms/MarkedText/MarkedText';
import { Number } from '@/components/Atoms/Number/Number';
import { SectionHeading } from '@/components/Atoms/SectionHeading/SectionHeading';
import { H5 } from '@/components/Core/Heading';
import { ContentItemCard } from '@/components/Organisms/ContentItemCard/ContentItemCard';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import useTranslation from '@/hooks/useTranslation';
import type { ContentType, DatasetType, IAnalyticsTraceableElement, IContentItem } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import Link from 'next/link';
import type { MutableRefObject, ReactNode } from 'react';
import { useRef, useState } from 'react';
import styles from './ResultGroup.module.css';

/**
 *  A card containing a list of content results.
 */
export const ResultGroup = t0 => {
  const $ = _c(36);
  let analyticsBaseId;
  let asCard;
  let elementProps;
  let items;
  let seeAllItemsLink;
  let title;
  let totalCount;
  let wrapperRef;
  if ($[0] !== t0) {
    ({
      items,
      title,
      totalCount,
      seeAllItemsLink,
      analyticsBaseId,
      wrapperRef,
      asCard,
      ...elementProps
    } = t0);
    $[0] = t0;
    $[1] = analyticsBaseId;
    $[2] = asCard;
    $[3] = elementProps;
    $[4] = items;
    $[5] = seeAllItemsLink;
    $[6] = title;
    $[7] = totalCount;
    $[8] = wrapperRef;
  } else {
    analyticsBaseId = $[1];
    asCard = $[2];
    elementProps = $[3];
    items = $[4];
    seeAllItemsLink = $[5];
    title = $[6];
    totalCount = $[7];
    wrapperRef = $[8];
  }
  let cardCount;
  if (totalCount) {
    let t1;
    if ($[9] !== totalCount) {
      t1 = <span>(<Number value={totalCount} />)</span>;
      $[9] = totalCount;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    cardCount = t1;
  }
  const ref = useRef(null);
  const {
    t
  } = useTranslation("common");
  const [isVisible, setIsVisible] = useState(false);
  let t1;
  if ($[11] !== t) {
    t1 = t("Actions.view_all");
    $[11] = t;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== seeAllItemsLink || $[14] !== t1) {
    t2 = <Link href={seeAllItemsLink}>{t1}</Link>;
    $[13] = seeAllItemsLink;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const headingAction = t2;
  let t3;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (ratio, observer) => {
      setIsVisible(!!ratio);
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  useIntersectionObserver(ref, t3, 5, 0, wrapperRef);
  const t4 = seeAllItemsLink ? headingAction : null;
  const t5 = asCard && cardCount;
  let t6;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== title) {
    t6 = <div className={styles.resultInformation}><SectionHeading as="h3" actions={t4}>{title}{t5}</SectionHeading></div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = title;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== analyticsBaseId || $[22] !== asCard || $[23] !== isVisible || $[24] !== items) {
    let t8;
    if ($[26] !== analyticsBaseId || $[27] !== asCard || $[28] !== isVisible) {
      t8 = (item, i) => <li className={styles.resultItem} key={`results-${i}-${item.url}`} data-analytics-tracking={buildTrackingObject(TrackingAction.FollowLink, TrackingCategory.Navigation, `${analyticsBaseId}: Item ${i} (${isVisible ? "Above Fold" : "Below Fold"})`)}>{asCard ? <ContentItemCard withImage={false} withSaveButton={false} withSummary={false} withDates={false} withSavedSearches={false} item={item as IContentItem} headingLevel="h3" analyticsBaseId={analyticsBaseId} analyticsCategory={TrackingCategory.QuickSearch} /> : <a href={item.url} className={styles.link}>{item.subTitle && <p>{item.subTitle}</p>}<H5 as="h3"><MarkedText value={item.title} /></H5></a>}</li>;
      $[26] = analyticsBaseId;
      $[27] = asCard;
      $[28] = isVisible;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    t7 = items.map(t8);
    $[21] = analyticsBaseId;
    $[22] = asCard;
    $[23] = isVisible;
    $[24] = items;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[30] !== t7) {
    t8 = <ul>{t7}</ul>;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== elementProps || $[33] !== t6 || $[34] !== t8) {
    t9 = <div {...elementProps} ref={ref}>{t6}{t8}</div>;
    $[32] = elementProps;
    $[33] = t6;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
};
interface ISearchElement {
  id?: string;
  type?: ContentType | DatasetType;
  title: string;
  url: string;
  summary?: string;
  displayDate?: string;
  subTitle?: string;
}
interface IResultGroupCardProps extends IAnalyticsTraceableElement {
  /** List of items. */
  items: ISearchElement[];
  /** Title of the result set. */
  title: string;
  /** Count of the total results. */
  totalCount?: number;
  className?: string;
  seeAllItemsLink?: string;
  asCard?: boolean;
  wrapperRef?: MutableRefObject<HTMLElement>;
}